import React, { useState, useEffect, useMemo } from 'react';
import { 
    Users, Clock, ArrowRight, Search, Zap, CheckCircle2,
    AlertTriangle, TrendingDown, RefreshCw, Filter, 
    Footprints, ChevronDown, ChevronRight, ChevronLeft, Play, Info,
    UserCheck, UserX, Compass, Smartphone, Monitor,
    Sparkles, Target, Award, BarChart3, Layers,
    ArrowUpDown, ArrowDown, ArrowUp, Calendar, CalendarRange
} from 'lucide-react';
import { 
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, 
    CartesianGrid, Tooltip, Legend, AreaChart, Area, Cell
} from 'recharts';
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';
import { useThemeTokens } from '../../hooks/useActiveTheme';

// Consistent Visual Palette
const COLOR_THEME = {
    primary: '#4338ca', // Indigo 700
    success: '#059669', // Emerald 600
    danger: '#e11d48',  // Rose 600
    warning: '#ea580c', // Orange 600
    info: '#2563eb',    // Blue 600
    neutral: '#64748b'  // Slate 500
};

// Reusable Pagination & Sorting Component
interface PaginationBarProps {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    sortOrder: 'desc' | 'asc';
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    onSortChange: (sort: 'desc' | 'asc') => void;
    label: string;
    isLoading?: boolean;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
    currentPage,
    totalPages,
    totalRecords,
    limit,
    sortOrder,
    onPageChange,
    onLimitChange,
    onSortChange,
    label,
    isLoading = false
}) => {
    const startIdx = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endIdx = Math.min(currentPage * limit, totalRecords);

    // Calculate page range to show (e.g. 1 ... 4 5 6 ... 10)
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            if (!pages.includes(totalPages)) pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs font-semibold">
            {/* Record Summary */}
            <div className="flex flex-wrap items-center gap-3 text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5">
                    {isLoading && <RefreshCw size={12} className="animate-spin text-indigo-600 dark:text-indigo-400" />}
                    <span>
                        Showing <strong className="text-slate-800 dark:text-slate-200">{startIdx}–{endIdx}</strong> of <strong className="text-slate-900 dark:text-slate-100">{totalRecords}</strong> {label}
                    </span>
                </span>

                <span className="text-slate-300 dark:text-slate-700">|</span>

                {/* Rows per page selector */}
                <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">Per page:</span>
                    <select
                        value={limit}
                        disabled={isLoading}
                        onChange={(e) => onLimitChange(Number(e.target.value))}
                        className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 dark:text-slate-200 outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
                    >
                        <option value={25}>25</option>
                        <option value={50}>50 (Default)</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                </div>
            </div>

            {/* Pagination Buttons & Sort Toggle */}
            <div className="flex flex-wrap items-center gap-2">
                {/* Sort Toggle Button */}
                <button
                    onClick={() => onSortChange(sortOrder === 'desc' ? 'asc' : 'desc')}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 transition-all active:scale-95 disabled:opacity-50"
                    title={sortOrder === 'desc' ? 'Currently Newest First. Click to sort Oldest First.' : 'Currently Oldest First. Click to sort Newest First.'}
                >
                    {sortOrder === 'desc' ? (
                        <>
                            <ArrowDown size={12} className="text-indigo-600 dark:text-indigo-400" />
                            <span>Newest First</span>
                        </>
                    ) : (
                        <>
                            <ArrowUp size={12} className="text-indigo-600 dark:text-indigo-400" />
                            <span>Oldest First</span>
                        </>
                    )}
                </button>

                {/* Prev Button */}
                <button
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={isLoading || currentPage <= 1}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                    title="Previous page"
                >
                    <ChevronLeft size={14} />
                </button>

                {/* Page number buttons */}
                <div className="flex items-center gap-1">
                    {getPageNumbers().map((p, idx) => {
                        if (p === '...') {
                            return (
                                <span key={`ellipsis-${idx}`} className="px-1.5 text-slate-400 text-xs">
                                    …
                                </span>
                            );
                        }
                        const isCurrent = p === currentPage;
                        return (
                            <button
                                key={p}
                                disabled={isLoading}
                                onClick={() => onPageChange(p as number)}
                                className={`w-7 h-7 rounded-lg text-xs font-black transition-all ${
                                    isCurrent
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                                } disabled:opacity-50`}
                            >
                                {p}
                            </button>
                        );
                    })}
                </div>

                {/* Next Button */}
                <button
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={isLoading || currentPage >= totalPages}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-all"
                    title="Next page"
                >
                    <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
};

const CustomFunnelTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const item = payload[0].payload;
        return (
            <div className="bg-slate-900 border border-slate-800 text-white p-4 rounded-2xl shadow-xl space-y-3.5 text-xs text-left max-w-xs">
                <div>
                    <span className="text-[8px] font-black uppercase tracking-widest text-indigo-400 block mb-0.5">Stage {item.step}</span>
                    <h4 className="font-black text-slate-200 text-xs leading-tight">{item.friendlyName || item.label}</h4>
                </div>
                
                <div className="space-y-2 border-t border-slate-800 pt-2.5 text-[11px]">
                    <div className="flex justify-between items-center gap-4">
                        <span className="text-slate-400 font-semibold">Active Count</span>
                        <span className="font-mono font-black text-slate-100">{item.count} visitors</span>
                    </div>

                    {item.step !== 'Registered' && item.step !== 'GuestLanding' && (
                        <>
                            <div className="flex justify-between items-center gap-4">
                                <span className="text-slate-400 font-semibold">Step-to-Step Conversion</span>
                                <span className="font-mono font-black text-indigo-400">
                                    {item.consecutiveConversion}%
                                </span>
                            </div>
                            <div className="flex justify-between items-center gap-4">
                                <span className="text-slate-400 font-semibold">Step-to-Step Drop-off</span>
                                <span className="font-mono font-black text-rose-400">
                                    -{item.consecutiveDropoff}%
                                </span>
                            </div>
                        </>
                    )}

                    <div className="flex justify-between items-center gap-4 border-t border-dashed border-slate-800 pt-2">
                        <span className="text-slate-400 font-semibold">Cumulative Retention</span>
                        <span className="font-mono font-black text-emerald-400">
                            {item.percentage}%
                        </span>
                    </div>
                </div>

                {item.step !== 'Registered' && item.step !== 'GuestLanding' && (
                    <div className="border-t border-slate-800 pt-2 text-[10px] text-slate-400 font-medium leading-normal">
                        {item.consecutiveConversion < 60 ? (
                            <span className="text-rose-400 font-extrabold flex gap-1 items-start">
                                ⚠️ High friction! Loss of {item.previousCount - item.count} users between stages.
                            </span>
                        ) : (
                            <span className="text-emerald-400 font-extrabold flex gap-1 items-start">
                                ✓ Stable transition rate for active visitors.
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    }
    return null;
};

export const UserJourneyAuditor: React.FC = () => {
    const tokens = useThemeTokens();
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Date range & preset filter states
    type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'custom';
    const [datePreset, setDatePreset] = useState<DatePreset>('14d');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [customStart, setCustomStart] = useState<string>(() => {
        const d = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
        return d.toISOString().split('T')[0];
    });
    const [customEnd, setCustomEnd] = useState<string>(() => {
        return new Date().toISOString().split('T')[0];
    });
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // View Mode Selector: 'guests' vs 'registered'
    const [activeCohort, setActiveCohort] = useState<'guests' | 'registered'>('guests');

    // Search & filters for Registered Users
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'Dropped off' | 'Active Engagement'>('all');
    const [expandedUser, setExpandedUser] = useState<string | null>(null);
    
    // Pagination & sorting for Registered Users
    const [registeredPage, setRegisteredPage] = useState<number>(1);
    const [registeredLimit, setRegisteredLimit] = useState<number>(50);
    const [registeredSort, setRegisteredSort] = useState<'desc' | 'asc'>('desc');

    // Search & filters for Guest Users
    const [guestSearchTerm, setGuestSearchTerm] = useState('');
    const [guestConversionFilter, setGuestConversionFilter] = useState<'all' | 'converted' | 'unconverted' | 'active'>('all');
    const [expandedGuest, setExpandedGuest] = useState<string | null>(null);

    // Pagination & sorting for Guest Users
    const [guestPage, setGuestPage] = useState<number>(1);
    const [guestLimit, setGuestLimit] = useState<number>(50);
    const [guestSort, setGuestSort] = useState<'desc' | 'asc'>('desc');

    const handlePresetChange = (preset: DatePreset) => {
        setDatePreset(preset);
        setGuestPage(1);
        setRegisteredPage(1);
        if (preset === 'custom') {
            setStartDate(customStart);
            setEndDate(customEnd);
        } else {
            setStartDate('');
            setEndDate('');
        }
    };

    const handleApplyCustomRange = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!customStart || !customEnd) return;
        setStartDate(customStart);
        setEndDate(customEnd);
        setGuestPage(1);
        setRegisteredPage(1);
    };

    const activeDateRangeLabel = useMemo(() => {
        const fmt = (dStr: string) => {
            if (!dStr) return '';
            const d = new Date(dStr.includes('T') ? dStr : `${dStr}T00:00:00`);
            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        };
        const now = new Date();
        if (datePreset === 'today') {
            return `Today (${now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`;
        }
        if (datePreset === 'yesterday') {
            const y = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            return `Yesterday (${y.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`;
        }
        if (datePreset === '7d') {
            const s = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return `Past 7 Days (${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`;
        }
        if (datePreset === '14d') {
            const s = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
            return `Past 14 Days (${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`;
        }
        if (datePreset === '30d') {
            const s = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return `Past 30 Days (${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${now.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`;
        }
        if (datePreset === 'custom') {
            if (startDate && endDate) {
                return `Custom (${fmt(startDate)} – ${fmt(endDate)})`;
            }
            return 'Custom Range';
        }
        return 'Active Telemetry Window';
    }, [datePreset, startDate, endDate]);

    const loadJourneyData = async (silent = false) => {
        if (!silent) setIsLoading(true);
        else setIsRefreshing(true);
        setError(null);
        try {
            const options: any = {
                preset: datePreset,
                registeredPage,
                registeredLimit,
                registeredSort,
                guestPage,
                guestLimit,
                guestSort
            };

            let daysParam = 14;
            if (datePreset === 'today') {
                options.preset = 'today';
                daysParam = 1;
            } else if (datePreset === 'yesterday') {
                options.preset = 'yesterday';
                daysParam = 1;
            } else if (datePreset === '7d') {
                options.preset = '7d';
                daysParam = 7;
            } else if (datePreset === '14d') {
                options.preset = '14d';
                daysParam = 14;
            } else if (datePreset === '30d') {
                options.preset = '30d';
                daysParam = 30;
            } else if (datePreset === 'custom') {
                options.preset = 'custom';
                if (startDate) options.startDate = startDate;
                if (endDate) options.endDate = endDate;
            }

            const res = await StorageService.getUserJourneyAuditorData(daysParam, 'all', options);
            setData(res);
        } catch (err: any) {
            console.error("Failed to fetch journey analytics", err);
            setError("Could not harvest active user journey logs. Database telemetry offline.");
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadJourneyData(data !== null);
    }, [datePreset, startDate, endDate, registeredPage, registeredLimit, registeredSort, guestPage, guestLimit, guestSort]);

    // Handle toggle expand registered user
    const toggleUserExpand = (userId: string) => {
        setExpandedUser(prev => prev === userId ? null : userId);
    };

    // Handle toggle expand guest
    const toggleGuestExpand = (guestId: string) => {
        setExpandedGuest(prev => prev === guestId ? null : guestId);
    };

    // Helper to identify first practice event in a timeline
    const getFirstPracticeEvent = (user: any) => {
        if (!user || !user.timeline || !Array.isArray(user.timeline)) return null;
        return user.timeline.find((ev: any) => 
            ev.isPracticeEvent || 
            (ev.event && /flipped|flashcard|practice|quiz|exam|answered|audio|podcast|scenario|simulation/i.test(ev.event) && !/landed|visited|left|abandoned|diagnostic/i.test(ev.event))
        );
    };

    // Filtered registered users list
    const filteredUsers = useMemo(() => {
        if (!data || !data.users) return [];
        return data.users.filter((u: any) => {
            const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                  u.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'all' || 
                                  (statusFilter === 'Dropped off' && (u.status === 'Dropped off' || u.status === 'Dropped')) ||
                                  (statusFilter === 'Active Engagement' && (u.status === 'Active Engagement' || u.status === 'Active'));
            return matchesSearch && matchesStatus;
        });
    }, [data, searchTerm, statusFilter]);

    // Filtered guest users list
    const filteredGuests = useMemo(() => {
        if (!data || !data.guests) return [];
        return data.guests.filter((g: any) => {
            const term = guestSearchTerm.toLowerCase();
            const matchesSearch = 
                (g.name && g.name.toLowerCase().includes(term)) ||
                (g.sessionId && g.sessionId.toLowerCase().includes(term)) ||
                (g.convertedEmail && g.convertedEmail.toLowerCase().includes(term)) ||
                (g.initialLandingRoute && g.initialLandingRoute.toLowerCase().includes(term)) ||
                (g.browser && g.browser.toLowerCase().includes(term));
            
            const matchesFilter = 
                guestConversionFilter === 'all' ||
                (guestConversionFilter === 'converted' && g.isConverted) ||
                (guestConversionFilter === 'unconverted' && !g.isConverted && g.conversionStatus !== 'Active Trial') ||
                (guestConversionFilter === 'active' && g.conversionStatus === 'Active Trial');

            return matchesSearch && matchesFilter;
        });
    }, [data, guestSearchTerm, guestConversionFilter]);

    // Consecutive and Cumulative Conversion rates for Registered cohort
    const registeredFunnelWithConsecutive = useMemo(() => {
        if (!data || !data.funnel) return [];
        return data.funnel.map((item: any, index: number) => {
            const previousItem = index > 0 ? data.funnel[index - 1] : null;
            let consecutiveConversion = 100;
            if (previousItem) {
                consecutiveConversion = previousItem.count > 0 
                    ? Math.min(100, Math.round((item.count / previousItem.count) * 100))
                    : 0;
            }
            const consecutiveDropoff = 100 - consecutiveConversion;
            const stepNameMapping: Record<string, string> = {
                'Registered': '1. Account Created',
                'DiagnosticStart': '2. Diagnostic Promoted',
                'DiagnosticComplete': '3. Diagnostic Completed/Skipped',
                'DashboardLand': '4. Landed on Dashboard',
                'HubNavigate': '5. Browsed Course Syllabus',
                'ClinicalPractice': '6. Practice Quiz Initiated'
            };
            const friendlyName = stepNameMapping[item.step] || item.label || item.step;
            return {
                ...item,
                friendlyName,
                consecutiveConversion,
                consecutiveDropoff,
                previousCount: previousItem ? previousItem.count : item.count
            };
        });
    }, [data]);

    // Consecutive and Cumulative Conversion rates for Guest cohort
    const guestFunnelWithConsecutive = useMemo(() => {
        if (!data || !data.guestSummary?.guestFunnel) return [];
        return data.guestSummary.guestFunnel.map((item: any, index: number) => {
            const previousItem = index > 0 ? data.guestSummary.guestFunnel[index - 1] : null;
            let consecutiveConversion = 100;
            if (previousItem) {
                consecutiveConversion = previousItem.count > 0 
                    ? Math.min(100, Math.round((item.count / previousItem.count) * 100))
                    : 0;
            }
            const consecutiveDropoff = 100 - consecutiveConversion;
            const stepNameMapping: Record<string, string> = {
                'GuestLanding': '1. Arrived on Website',
                'ContentExplore': '2. Browsed Practice/Syllabus',
                'PracticeAttempts': '3. Attempted Practice Drills',
                'SignupPrompt': '4. Encountered Signup Prompt/Limit',
                'Converted': '5. Converted (Signed Up)'
            };
            const friendlyName = stepNameMapping[item.step] || item.label || item.step;
            return {
                ...item,
                friendlyName,
                consecutiveConversion,
                consecutiveDropoff,
                previousCount: previousItem ? previousItem.count : item.count
            };
        });
    }, [data]);

    if (isLoading) {
        return <PageLoader message="Compiling user & guest session footprints..." />;
    }

    if (error || !data) {
        return (
            <div className="p-8 bg-white dark:bg-slate-900 border border-rose-100 dark:border-rose-900/30 rounded-[2.5rem] shadow-xl text-center max-w-xl mx-auto my-12 space-y-6">
                <div className="w-16 h-16 bg-rose-50 dark:bg-rose-950/40 text-rose-600 rounded-3xl flex items-center justify-center mx-auto">
                    <AlertTriangle size={32} />
                </div>
                <div>
                    <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 mb-2">Audit System Offline</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed font-semibold">
                        {error || "An error occurred while loading telemetry datasets."}
                    </p>
                </div>
                <button 
                    onClick={() => loadJourneyData()}
                    className={`px-6 py-3 text-white rounded-xl text-xs font-black tracking-widest uppercase transition-all shadow-md active:scale-95 ${tokens.primaryBtn}`}
                >
                    Retry Ingestion
                </button>
            </div>
        );
    }

    const guestSummary = data.guestSummary || {};

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-12">
            {/* Header section with inline action keys */}
            <div className="bg-white dark:bg-slate-900 p-6 border border-slate-200 dark:border-slate-800 rounded-[2rem] shadow-sm space-y-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <span className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 mb-1 text-indigo-600 dark:text-indigo-400">
                            <Footprints size={14} /> Telemetry & User Journeys Auditor
                        </span>
                        <h1 className={`text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight ${tokens.headerFont}`}>
                            User Journeys Audit Logs
                        </h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold mt-1">
                            Track guest user exploration, practice attempts, time spent, and onboarding conversions in real-time.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 self-start lg:self-auto">
                        {/* Active Date Range Tag */}
                        <div className="px-3.5 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-200 shadow-sm">
                            <CalendarRange size={14} className="text-indigo-600 dark:text-indigo-400 shrink-0" />
                            <span className="text-[11px] font-black">{activeDateRangeLabel}</span>
                        </div>

                        <button
                            onClick={() => loadJourneyData(true)}
                            disabled={isRefreshing}
                            className="p-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl transition-all active:scale-95 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
                            title="Re-fetch analytics logs"
                        >
                            <RefreshCw size={15} className={`transition-transform duration-700 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Date Filter Bar with Presets & Date Range */}
                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-1.5 p-1.5 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                        {[
                            { id: 'today', label: 'Today' },
                            { id: 'yesterday', label: 'Yesterday' },
                            { id: '7d', label: '7 Days' },
                            { id: '14d', label: '14 Days' },
                            { id: '30d', label: '30 Days' },
                            { id: 'custom', label: 'Date Range' }
                        ].map((preset) => {
                            const isSelected = datePreset === preset.id;
                            return (
                                <button
                                    key={preset.id}
                                    onClick={() => handlePresetChange(preset.id as DatePreset)}
                                    className={`px-3.5 py-2 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all ${
                                        isSelected
                                            ? 'bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-400 shadow-sm border border-slate-200 dark:border-slate-700'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Custom Date Range Picker Inputs */}
                    {datePreset === 'custom' && (
                        <form onSubmit={handleApplyCustomRange} className="flex flex-wrap items-center gap-2.5 animate-in fade-in duration-200">
                            <div className="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 gap-2 shadow-sm">
                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">From</span>
                                <input
                                    type="date"
                                    value={customStart}
                                    max={customEnd || undefined}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="bg-transparent text-xs font-bold text-slate-800 dark:text-slate-200 focus:outline-none cursor-pointer"
                                />
                            </div>

                            <div className="flex items-center bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 gap-2 shadow-sm">
                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">To</span>
                                <input
                                    type="date"
                                    value={customEnd}
                                    min={customStart || undefined}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="bg-transparent text-xs font-bold text-slate-800 dark:text-slate-200 focus:outline-none cursor-pointer"
                                />
                            </div>

                            <button
                                type="submit"
                                className={`px-4 py-2 text-white font-extrabold text-[10px] tracking-widest uppercase rounded-xl transition-all active:scale-95 shadow-sm ${tokens.primaryBtn}`}
                            >
                                Apply Range
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* Audience Switcher Tabs: Guest Users vs Registered Students */}
            <div className="flex items-center gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 max-w-xl">
                <button
                    onClick={() => setActiveCohort('guests')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeCohort === 'guests'
                            ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-md border border-slate-200 dark:border-slate-700'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
                    }`}
                >
                    <Compass size={16} className={activeCohort === 'guests' ? 'text-indigo-600 dark:text-indigo-400' : ''} />
                    <span>Guest User Journeys ({data?.pagination?.guests?.total || guestSummary.totalGuests || (data.guests || []).length})</span>
                    {guestSummary.convertedGuests > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] bg-emerald-500 text-white font-extrabold">
                            {guestSummary.convertedGuests} Converted
                        </span>
                    )}
                </button>

                <button
                    onClick={() => setActiveCohort('registered')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-black text-xs uppercase tracking-wider transition-all ${
                        activeCohort === 'registered'
                            ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-md border border-slate-200 dark:border-slate-700'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-300'
                    }`}
                >
                    <Users size={16} className={activeCohort === 'registered' ? 'text-indigo-600 dark:text-indigo-400' : ''} />
                    <span>Registered Cohort ({data?.pagination?.registered?.total || data.users?.length || 0})</span>
                </button>
            </div>

            {/* VIEW 1: GUEST USER JOURNEYS & CONVERSION ANALYTICS */}
            {activeCohort === 'guests' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    {/* Top KPI Metrics Bar for Guest Explorers */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Total Guests */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">
                            <div className="flex items-center justify-between text-slate-400">
                                <span className="text-[10px] font-black uppercase tracking-wider">Total Guest Visitors</span>
                                <Compass size={16} className="text-indigo-500" />
                            </div>
                            <p className="text-2xl font-black text-slate-900 dark:text-slate-100 tabular-nums">
                                {guestSummary.totalGuests || 0}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400">100% Top of Funnel</p>
                        </div>

                        {/* Guest Practice Attempts */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">
                            <div className="flex items-center justify-between text-slate-400">
                                <span className="text-[10px] font-black uppercase tracking-wider">Practice Attempts</span>
                                <Zap size={16} className="text-amber-500" />
                            </div>
                            <p className="text-2xl font-black text-slate-900 dark:text-slate-100 tabular-nums">
                                {guestSummary.totalGuestAttempts || 0}
                            </p>
                            <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                                ~{guestSummary.avgGuestAttempts || 0} items / visitor
                            </p>
                        </div>

                        {/* Average Time Spent */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-2">
                            <div className="flex items-center justify-between text-slate-400">
                                <span className="text-[10px] font-black uppercase tracking-wider">Avg Time Spent</span>
                                <Clock size={16} className="text-indigo-500" />
                            </div>
                            <p className="text-2xl font-black text-slate-900 dark:text-slate-100 tabular-nums">
                                {guestSummary.avgGuestTimeSpent || '0s'}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400">Active site duration</p>
                        </div>

                        {/* Converted (Signed Up) */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-emerald-200 dark:border-emerald-900/40 shadow-sm space-y-2 bg-emerald-50/20">
                            <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
                                <span className="text-[10px] font-black uppercase tracking-wider">Converted (Signed Up)</span>
                                <UserCheck size={16} className="text-emerald-600" />
                            </div>
                            <div className="flex items-baseline gap-2">
                                <p className="text-2xl font-black text-emerald-700 dark:text-emerald-300 tabular-nums">
                                    {guestSummary.convertedGuests || 0}
                                </p>
                                <span className="text-xs font-black text-emerald-600 bg-emerald-100 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full">
                                    {guestSummary.conversionRate || 0}% Rate
                                </span>
                            </div>
                            <p className="text-[10px] font-bold text-emerald-600/80">Created full account</p>
                        </div>

                        {/* Did Not Sign Up */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-rose-200 dark:border-rose-900/40 shadow-sm space-y-2 bg-rose-50/20 col-span-2 lg:col-span-1">
                            <div className="flex items-center justify-between text-rose-600 dark:text-rose-400">
                                <span className="text-[10px] font-black uppercase tracking-wider">Did Not Sign Up</span>
                                <UserX size={16} className="text-rose-600" />
                            </div>
                            <div className="flex items-baseline gap-2">
                                <p className="text-2xl font-black text-rose-700 dark:text-rose-300 tabular-nums">
                                    {guestSummary.unconvertedGuests || 0}
                                </p>
                                <span className="text-xs font-black text-rose-600 bg-rose-100 dark:bg-rose-950/60 px-2 py-0.5 rounded-full">
                                    {Math.round(100 - (guestSummary.conversionRate || 0))}% Drop
                                </span>
                            </div>
                            <p className="text-[10px] font-bold text-rose-600/80">Left without registering</p>
                        </div>
                    </div>

                    {/* Explanatory Callout regarding Guest Conversion & Attempts */}
                    <div className="p-6 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 rounded-[2rem] flex items-start gap-4">
                        <div className="p-3 bg-indigo-600 text-white rounded-2xl shrink-0 shadow-lg shadow-indigo-100">
                            <Sparkles size={22} strokeWidth={2.5} />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-sm font-black text-slate-900 dark:text-slate-100">Guest Route & Trial Engagement Footprint</h4>
                            <p className="text-xs text-slate-600 dark:text-slate-300 font-semibold leading-relaxed">
                                Guests entering directly from <code className="bg-white dark:bg-slate-900 px-2 py-0.5 rounded-md border border-indigo-200 dark:border-indigo-800 font-mono text-[11px] font-bold">/cna/practice</code> or external portal gateways complete an average of <strong className="text-indigo-700 dark:text-indigo-400 font-black">{guestSummary.avgGuestAttempts || 0} practice questions</strong> with a <strong className="text-emerald-700 dark:text-emerald-400 font-black">{guestSummary.conversionRate || 0}% conversion rate</strong> into fully registered student accounts.
                            </p>
                        </div>
                    </div>

                    {/* Guest Funnel & Drop-off Analysis */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Guest Conversion Funnel */}
                        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-[2.5rem] shadow-sm flex flex-col justify-between">
                            <div>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-6 gap-2">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider">Guest Trial & Conversion Funnel</h3>
                                        <p className="text-[10px] text-slate-400 font-bold mt-0.5">Consecutive conversion efficiency from initial guest landing to student registration</p>
                                    </div>
                                    <span className="self-start sm:self-center px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-900/50 text-indigo-700 dark:text-indigo-400 rounded-lg text-[9px] font-black uppercase tracking-wider">
                                        Guest Funnel Telemetry
                                    </span>
                                </div>
                                
                                {/* Step Indicators */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
                                    {guestFunnelWithConsecutive.map((f: any, idx: number) => {
                                        const isHighDrop = idx > 0 && f.consecutiveConversion < 60;
                                        return (
                                            <div 
                                                key={idx} 
                                                className={`p-3 rounded-2xl border transition-all flex flex-col justify-between relative ${
                                                    isHighDrop 
                                                        ? 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/30' 
                                                        : f.step === 'Converted' 
                                                            ? 'bg-emerald-50/45 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/30' 
                                                            : 'bg-slate-50 dark:bg-slate-800/60 border-slate-100 dark:border-slate-800'
                                                }`}
                                            >
                                                <div>
                                                    <p className="text-[8px] font-black uppercase text-slate-400 tracking-wider mb-2 truncate" title={f.friendlyName}>
                                                        {f.step}
                                                    </p>
                                                    <p className="text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight leading-none mb-1 tabular-nums">
                                                        {f.count}
                                                    </p>
                                                </div>
                                                
                                                <div className="border-t border-slate-200/60 dark:border-slate-700/60 pt-2 mt-2 space-y-1">
                                                    <div className="flex justify-between items-center text-[9px]">
                                                        <span className="text-slate-400 font-bold">Total:</span>
                                                        <span className="font-mono font-black text-emerald-600 dark:text-emerald-400">{f.percentage}%</span>
                                                    </div>
                                                    {idx > 0 && (
                                                        <div className="flex justify-between items-center text-[9px]">
                                                            <span className="text-slate-400 font-bold">Step:</span>
                                                            <span className={`font-mono font-black ${isHighDrop ? 'text-rose-500' : 'text-indigo-600 dark:text-indigo-400'}`}>
                                                                {f.consecutiveConversion}%
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Recharts Bar Visualization */}
                                <div className="h-64 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={guestFunnelWithConsecutive} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis 
                                                dataKey="step" 
                                                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} 
                                                axisLine={{ stroke: '#e2e8f0' }}
                                                tickLine={false}
                                            />
                                            <YAxis 
                                                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} 
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <Tooltip content={<CustomFunnelTooltip />} />
                                            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                                                {guestFunnelWithConsecutive.map((entry: any, index: number) => {
                                                    const color = index === guestFunnelWithConsecutive.length - 1 
                                                        ? COLOR_THEME.success 
                                                        : entry.consecutiveConversion < 60 && index > 0
                                                            ? COLOR_THEME.danger 
                                                            : COLOR_THEME.primary;
                                                    return <Cell key={`cell-${index}`} fill={color} />;
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Top Guest Drop-off Points */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-[2.5rem] shadow-sm flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-6">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider">Guest Friction Points</h3>
                                        <p className="text-[10px] text-slate-400 font-bold mt-0.5">Where guests stop and do not sign up</p>
                                    </div>
                                    <span className="p-2 bg-rose-50 dark:bg-rose-950/40 text-rose-600 rounded-xl">
                                        <TrendingDown size={16} />
                                    </span>
                                </div>

                                <div className="space-y-4">
                                    {(guestSummary.guestDropoffs || []).map((drop: any, idx: number) => (
                                        <div key={idx} className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-1.5">
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span className="font-extrabold text-slate-800 dark:text-slate-200">
                                                    {idx + 1}. {drop.stage}
                                                </span>
                                                <span className="font-black text-rose-600 font-mono tracking-tight shrink-0 bg-rose-50 dark:bg-rose-950/60 border border-rose-100 dark:border-rose-900/50 px-2 py-0.5 rounded text-[10px]">
                                                    {drop.percentage}% drop ({drop.dropCount})
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                                                {drop.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Guest User Journeys Audit Logs Table */}
                    <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-6 shadow-sm min-h-[380px]">
                        {/* Interactive Loading Backdrop during page/filter transitions */}
                        {isRefreshing && (
                            <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[1.5px] z-20 flex flex-col items-center justify-center gap-3 rounded-[2.5rem] transition-all animate-in fade-in duration-150">
                                <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100">
                                    <RefreshCw size={18} className="animate-spin text-indigo-600 dark:text-indigo-400" />
                                    <span className="text-xs font-black tracking-wide">
                                        Loading Guest Footprints...
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4 mb-6">
                            <div>
                                <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider">Guest User Journeys Audit Logs</h3>
                                <p className="text-[10px] text-slate-400 font-bold mt-0.5">Chronological footprint traces of guest attempts, time spent, and conversion status.</p>
                            </div>

                            <div className="flex flex-wrap items-center gap-3">
                                {/* Search */}
                                <div className="relative">
                                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        value={guestSearchTerm}
                                        onChange={(e) => setGuestSearchTerm(e.target.value)}
                                        placeholder="Search by session, email, device..."
                                        className="pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 w-[240px]"
                                    />
                                </div>

                                {/* Conversion filter */}
                                <div className="flex border border-slate-200 dark:border-slate-700 rounded-xl p-1 bg-slate-50 dark:bg-slate-800 shrink-0">
                                    <button
                                        onClick={() => setGuestConversionFilter('all')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${guestConversionFilter === 'all' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm border border-slate-200 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        All ({data?.pagination?.guests?.total || data.guests?.length || 0})
                                    </button>
                                    <button
                                        onClick={() => setGuestConversionFilter('converted')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${guestConversionFilter === 'converted' ? 'bg-white dark:bg-slate-900 text-emerald-600 shadow-sm border border-slate-100 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        Converted ({guestSummary.convertedGuests || 0})
                                    </button>
                                    <button
                                        onClick={() => setGuestConversionFilter('unconverted')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${guestConversionFilter === 'unconverted' ? 'bg-white dark:bg-slate-900 text-rose-600 shadow-sm border border-slate-100 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        Did Not Sign Up ({guestSummary.unconvertedGuests || 0})
                                    </button>
                                    <button
                                        onClick={() => setGuestConversionFilter('active')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${guestConversionFilter === 'active' ? 'bg-white dark:bg-slate-900 text-indigo-600 shadow-sm border border-slate-100 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        Active Trial
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Guest Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px] text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-100 dark:border-slate-800 text-slate-400 font-black uppercase tracking-widest text-[9px]">
                                        <th className="py-4 px-4 w-[200px]">Guest Session</th>
                                        <th className="py-4 px-4 w-[160px]">First Landed Route</th>
                                        <th className="py-4 px-4 w-[150px]">Conversion Status</th>
                                        <th className="py-4 px-4 w-[140px]">Practice Attempts</th>
                                        <th className="py-4 px-4 w-[130px]">Time Spent</th>
                                        <th className="py-4 px-4 w-[180px]">Last Step / Outcome</th>
                                        <th className="py-4 px-4 text-right">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 dark:divide-slate-800/60 font-semibold text-slate-700 dark:text-slate-300">
                                    {filteredGuests.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="py-12 text-center text-slate-400 font-bold">
                                                No guest footprints match your filtering parameters.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredGuests.map((g: any) => {
                                            const isExpanded = expandedGuest === g.userId;
                                            const joinDate = new Date(g.joinedAt).toLocaleString();

                                            return (
                                                <React.Fragment key={g.userId}>
                                                    <tr className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors ${isExpanded ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''}`}>
                                                        <td className="py-4 px-4">
                                                            <div>
                                                                <p className="font-extrabold text-slate-900 dark:text-slate-100 leading-tight flex items-center gap-1.5">
                                                                    {g.deviceType === 'mobile' ? <Smartphone size={12} className="text-slate-400" /> : <Monitor size={12} className="text-slate-400" />}
                                                                    {g.name}
                                                                </p>
                                                                <p className="text-[10px] text-slate-400 font-semibold font-mono truncate max-w-[180px]">
                                                                    {g.isConverted ? g.convertedEmail : g.sessionId}
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-slate-600 dark:text-slate-400 font-bold">
                                                                {g.initialLandingRoute || '/cna/practice'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${
                                                                g.isConverted
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                                                                    : g.conversionStatus === 'Active Trial'
                                                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800'
                                                                        : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
                                                            }`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${
                                                                    g.isConverted ? 'bg-emerald-500' : g.conversionStatus === 'Active Trial' ? 'bg-indigo-500' : 'bg-rose-500'
                                                                }`} />
                                                                {g.conversionStatus}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center gap-1.5 text-slate-800 dark:text-slate-200 font-bold tabular-nums">
                                                                <Zap size={12} className="text-amber-500 fill-amber-500" />
                                                                <span>{g.questionsAnswered || 0} Qs</span>
                                                                {g.flashcardsViewed > 0 && <span className="text-slate-400 text-[10px]">({g.flashcardsViewed} FC)</span>}
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50/80 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50 rounded-lg text-[10px] font-extrabold tabular-nums">
                                                                <Clock size={11} className="text-indigo-500" />
                                                                {g.totalTimeSpent || '0s'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <p className="text-slate-800 dark:text-slate-200 font-bold truncate max-w-[180px]" title={g.lastSuccessfulStep || g.currentStep}>
                                                                {g.lastSuccessfulStep || g.currentStep}
                                                            </p>
                                                        </td>
                                                        <td className="py-4 px-4 text-right">
                                                            <button
                                                                onClick={() => toggleGuestExpand(g.userId)}
                                                                className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 transition-all inline-flex items-center gap-1"
                                                            >
                                                                Timeline {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {/* Expandable Guest Action Timeline */}
                                                    {isExpanded && (
                                                        <tr className="bg-slate-50/30 dark:bg-slate-800/20">
                                                            <td colSpan={7} className="py-4 px-8 border-t border-slate-100 dark:border-slate-800">
                                                                <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-5 space-y-4 shadow-inner">
                                                                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2.5">
                                                                        <span className="text-[10px] font-black tracking-widest text-indigo-600 dark:text-indigo-400 uppercase flex items-center gap-1">
                                                                            <Play size={10} fill="currentColor" /> Guest Session Activity Footprints
                                                                        </span>
                                                                        <div className="flex items-center gap-3 text-[10px] font-bold text-slate-400 font-mono">
                                                                            <span>Device: {g.browser} ({g.deviceType})</span>
                                                                            <span>Session: {g.sessionId}</span>
                                                                        </div>
                                                                    </div>

                                                                    <div className="relative pl-6 border-l border-dashed border-indigo-200 dark:border-indigo-800 space-y-4 py-2 ml-2">
                                                                        {(g.timeline || []).map((ev: any, evIdx: number) => {
                                                                            const isLanding = evIdx === 0;
                                                                            const isConvertedEv = ev.category === 'conversion' || ev.event.includes('Converted');
                                                                            const isLimit = ev.category === 'conversion' && ev.event.includes('Limit');
                                                                            const stepColor = isConvertedEv ? '#059669' : isLimit ? '#ea580c' : ev.status === 'danger' ? '#e11d48' : '#4338ca';
                                                                            
                                                                            return (
                                                                                <div key={evIdx} className="relative flex items-start justify-between gap-4">
                                                                                    <span className="absolute -left-[31px] top-1 w-2.5 h-2.5 rounded-full border border-white dark:border-slate-900" style={{ backgroundColor: stepColor }} />
                                                                                    
                                                                                    <div className="space-y-0.5">
                                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                                            <p className="text-xs font-extrabold text-slate-900 dark:text-slate-100 leading-tight">{ev.event}</p>
                                                                                            {isConvertedEv && (
                                                                                                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 rounded-full text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1">
                                                                                                    <Sparkles size={10} className="fill-emerald-600 text-emerald-600" /> Account Created
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        {isLanding && <p className="text-[10px] text-slate-400 font-semibold leading-relaxed">T_0 timestamp: {new Date(ev.timestamp || g.joinedAt).toLocaleTimeString()}</p>}
                                                                                    </div>

                                                                                    <div className="text-right shrink-0 whitespace-nowrap">
                                                                                        <span className={`text-[10px] font-bold font-mono tracking-tight px-1.5 py-0.5 rounded border ${
                                                                                            isConvertedEv 
                                                                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 font-extrabold' 
                                                                                            : 'bg-indigo-50 text-indigo-600 border-indigo-100/50 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800'
                                                                                        }`}>
                                                                                            +{ev.relativeTime}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>

                                                                     {!g.isConverted && (
                                                                        <div className="flex items-center gap-2 p-3 bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 border border-rose-100 dark:border-rose-900/40 rounded-xl text-[10px]">
                                                                            <Info size={14} className="shrink-0 text-rose-500" />
                                                                            <span>
                                                                                <strong>Unconverted Guest Outcome:</strong> Visitor attempted {g.totalAttempts} study items across {g.totalTimeSpent} of session duration before closing the session without creating an account.
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Guest Pagination Bar */}
                        <div className="mt-4">
                            <PaginationBar
                                currentPage={data?.pagination?.guests?.page || guestPage}
                                totalPages={data?.pagination?.guests?.totalPages || Math.max(1, Math.ceil((data?.pagination?.guests?.total || guestSummary.totalGuests || (data.guests || []).length) / guestLimit))}
                                totalRecords={data?.pagination?.guests?.total || guestSummary.totalGuests || (data.guests || []).length}
                                limit={guestLimit}
                                sortOrder={guestSort}
                                isLoading={isRefreshing}
                                onPageChange={(p) => setGuestPage(p)}
                                onLimitChange={(l) => {
                                    setGuestLimit(l);
                                    setGuestPage(1);
                                }}
                                onSortChange={(s) => {
                                    setGuestSort(s);
                                    setGuestPage(1);
                                }}
                                label="guest footprints"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* VIEW 2: REGISTERED STUDENTS AUDIT LOGS */}
            {activeCohort === 'registered' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    {/* Explanatory Callout regarding Drop and Spikes */}
                    <div className="p-6 bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/40 rounded-[2rem] flex items-start gap-4">
                        <div className="p-3 bg-rose-500 text-white rounded-2xl shrink-0 shadow-lg shadow-rose-100">
                            <TrendingDown size={22} strokeWidth={2.5} />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-sm font-black text-slate-900 dark:text-slate-100">Immediate Churn Diagnostics: The Onboarding Barrier</h4>
                            <p className="text-xs text-slate-600 dark:text-slate-300 font-semibold leading-relaxed">
                                Data shows a <strong className="text-rose-700 dark:text-rose-400 font-extrabold pb-0.5 border-b-2 border-rose-200 dark:border-rose-800">Dangerous Drop-off during the Diagnostic Ready Check</strong>. Registered new users who enter the application often abandon it during the readiness prompt. Shortening or allowing users to skip directly preserves attention and converts them into active study modes.
                            </p>
                        </div>
                    </div>

                    {/* Core Funnel and Analysis Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Visual conversion funnel */}
                        <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-[2.5rem] shadow-sm flex flex-col justify-between">
                            <div>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-6 gap-2">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider">Registered Onboarding Funnel Step Conversion</h3>
                                        <p className="text-[10px] text-slate-400 font-bold mt-0.5">Consecutive conversion efficiency versus overall user drop-off points</p>
                                    </div>
                                    <span className="self-start sm:self-center px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-900/50 text-indigo-700 dark:text-indigo-400 rounded-lg text-[9px] font-black uppercase tracking-wider">
                                        Dual Metric Funnel Chart
                                    </span>
                                </div>
                                
                                {/* Horizontal Conversion Flow indicators */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                                    {registeredFunnelWithConsecutive.map((f: any, idx: number) => {
                                        const isHighDrop = idx > 0 && f.consecutiveConversion < 60;
                                        return (
                                            <div 
                                                key={idx} 
                                                className={`p-3 rounded-2xl border transition-all flex flex-col justify-between relative ${
                                                    isHighDrop 
                                                        ? 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/30' 
                                                        : f.step === 'ClinicalPractice' 
                                                            ? 'bg-emerald-50/45 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/30' 
                                                            : 'bg-slate-50 dark:bg-slate-800/60 border-slate-100 dark:border-slate-800'
                                                }`}
                                            >
                                                <div>
                                                    <p className="text-[8px] font-black uppercase text-slate-400 tracking-wider mb-2 truncate" title={f.label}>
                                                        {f.step}
                                                    </p>
                                                    <p className="text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight leading-none mb-1 tabular-nums">
                                                        {f.count}
                                                    </p>
                                                </div>
                                                
                                                <div className="border-t border-slate-200/60 dark:border-slate-700/60 pt-2 mt-2 space-y-1">
                                                    <div className="flex justify-between items-center text-[9px]">
                                                        <span className="text-slate-400 font-bold">Total:</span>
                                                        <span className="font-mono font-black text-emerald-600 dark:text-emerald-400">{f.percentage}%</span>
                                                    </div>
                                                    {idx > 0 && (
                                                        <div className="flex justify-between items-center text-[9px]">
                                                            <span className="text-slate-400 font-bold">Step:</span>
                                                            <span className={`font-mono font-black ${isHighDrop ? 'text-rose-500' : 'text-indigo-600 dark:text-indigo-400'}`}>
                                                                {f.consecutiveConversion}%
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Recharts Chart */}
                                <div className="h-64 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={registeredFunnelWithConsecutive} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis 
                                                dataKey="step" 
                                                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} 
                                                axisLine={{ stroke: '#e2e8f0' }}
                                                tickLine={false}
                                            />
                                            <YAxis 
                                                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }} 
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <Tooltip content={<CustomFunnelTooltip />} />
                                            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                                                {registeredFunnelWithConsecutive.map((entry: any, index: number) => {
                                                    const color = index === registeredFunnelWithConsecutive.length - 1 
                                                        ? COLOR_THEME.success 
                                                        : entry.consecutiveConversion < 60 && index > 0
                                                            ? COLOR_THEME.danger 
                                                            : COLOR_THEME.primary;
                                                    return <Cell key={`cell-${index}`} fill={color} />;
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Top drop off points for Registered */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-[2.5rem] shadow-sm flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-6">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider">Top Friction Points</h3>
                                        <p className="text-[10px] text-slate-400 font-bold mt-0.5">Highest onboarding churn stages</p>
                                    </div>
                                    <span className="p-2 bg-rose-50 dark:bg-rose-950/40 text-rose-600 rounded-xl">
                                        <TrendingDown size={16} />
                                    </span>
                                </div>

                                <div className="space-y-4">
                                    {(data?.dropoffs || []).map((drop: any, idx: number) => (
                                        <div key={idx} className="space-y-1">
                                            <div className="flex justify-between items-center text-[11px]">
                                                <span className="font-extrabold text-slate-800 dark:text-slate-200 truncate max-w-[200px]" title={drop.stage}>
                                                    {idx + 1}. {drop.stage}
                                                </span>
                                                <span className="font-black text-rose-600 font-mono tracking-tight shrink-0 bg-rose-50 dark:bg-rose-950/60 border border-rose-100 dark:border-rose-900/50 px-1.5 py-0.5 rounded text-[10px]">
                                                    +{drop.percentage}% drop
                                                </span>
                                            </div>
                                            <p className="text-[9px] text-slate-400 font-semibold leading-relaxed">
                                                {drop.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and User Journey Timeline Section */}
                    <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-6 shadow-sm min-h-[380px]">
                        {/* Interactive Loading Backdrop during page/filter transitions */}
                        {isRefreshing && (
                            <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[1.5px] z-20 flex flex-col items-center justify-center gap-3 rounded-[2.5rem] transition-all animate-in fade-in duration-150">
                                <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100">
                                    <RefreshCw size={18} className="animate-spin text-indigo-600 dark:text-indigo-400" />
                                    <span className="text-xs font-black tracking-wide">
                                        Loading Registered Journeys...
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4 mb-6">
                            <div>
                                <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider">Registered User Journeys Audit Logs</h3>
                                <p className="text-[10px] text-slate-400 font-bold mt-0.5">Explore chronological action timelines per newly registered student.</p>
                            </div>

                            <div className="flex flex-wrap items-center gap-3">
                                {/* Search field */}
                                <div className="relative">
                                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search by name or email..."
                                        className="pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 w-[220px]"
                                    />
                                </div>

                                {/* Status filter selection */}
                                <div className="flex border border-slate-200 dark:border-slate-700 rounded-xl p-1 bg-slate-50 dark:bg-slate-800 shrink-0">
                                    <button
                                        onClick={() => setStatusFilter('all')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${statusFilter === 'all' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm border border-slate-200 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => setStatusFilter('Dropped off')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${statusFilter === 'Dropped off' ? 'bg-white dark:bg-slate-900 text-rose-600 shadow-sm border border-slate-100 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        Dropped
                                    </button>
                                    <button
                                        onClick={() => setStatusFilter('Active Engagement')}
                                        className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all ${statusFilter === 'Active Engagement' ? 'bg-white dark:bg-slate-900 text-emerald-600 shadow-sm border border-slate-100 dark:border-slate-700' : 'text-slate-400 hover:text-slate-700'}`}
                                    >
                                        Active
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Registered Users Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px] text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-100 dark:border-slate-800 text-slate-400 font-black uppercase tracking-widest text-[9px]">
                                        <th className="py-4 px-4 w-[180px]">User Account</th>
                                        <th className="py-4 px-4 w-[160px]">Registration Time</th>
                                        <th className="py-4 px-4 w-[150px]">Current Status</th>
                                        <th className="py-4 px-4 w-[180px]">Last Successful Step</th>
                                        <th className="py-4 px-4 w-[120px]">Time to Practice</th>
                                        <th className="py-4 px-4 w-[130px]">Total Time Spent</th>
                                        <th className="py-4 px-4 text-right">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 dark:divide-slate-800/60 font-semibold text-slate-700 dark:text-slate-300">
                                    {filteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="py-12 text-center text-slate-400 font-bold">
                                                No journey footprints match your filtering parameters.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUsers.map((u: any) => {
                                            const isExpanded = expandedUser === u.userId;
                                            const joinDate = new Date(u.joinedAt).toLocaleString();
                                            const firstPracticeEv = getFirstPracticeEvent(u);
                                            const practiceDuration = firstPracticeEv ? firstPracticeEv.relativeTime : (u.durationToFirstFeature || null);

                                            return (
                                                <React.Fragment key={u.userId}>
                                                    <tr className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors ${isExpanded ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''}`}>
                                                        <td className="py-4 px-4">
                                                            <div>
                                                                <p className="font-extrabold text-slate-900 dark:text-slate-100 leading-tight">{u.name}</p>
                                                                <p className="text-[10px] text-slate-400 font-semibold pr-4 font-mono truncate">{u.email}</p>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-500 dark:text-slate-400 tabular-nums">{joinDate}</td>
                                                        <td className="py-4 px-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${
                                                                u.status === 'Active Engagement' || u.status === 'Active'
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' 
                                                                    : 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
                                                            }`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'Active Engagement' || u.status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                                {u.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-800 dark:text-slate-200 font-bold">{u.lastSuccessfulStep}</td>
                                                        <td className="py-4 px-4 text-slate-500 dark:text-slate-400 tabular-nums">
                                                            {practiceDuration ? (
                                                                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-extrabold">
                                                                    <Zap size={12} className="text-emerald-500 fill-emerald-500" /> {practiceDuration}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-300 dark:text-slate-600">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                                                            {u.totalTimeSpent && u.totalTimeSpent !== '0s' ? (
                                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50/80 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50 rounded-lg text-[10px] font-extrabold">
                                                                    <Clock size={11} className="text-indigo-500" />
                                                                    {u.totalTimeSpent}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-300 dark:text-slate-600 font-normal">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-4 px-4 text-right">
                                                            <button
                                                                onClick={() => toggleUserExpand(u.userId)}
                                                                className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 transition-all inline-flex items-center gap-1"
                                                            >
                                                                Timeline {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {/* Expandable Action Timeline trace detail */}
                                                    {isExpanded && (
                                                        <tr className="bg-slate-50/30 dark:bg-slate-800/20">
                                                            <td colSpan={7} className="py-4 px-8 border-t border-slate-100 dark:border-slate-800">
                                                                <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-5 space-y-4 shadow-inner">
                                                                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2.5">
                                                                        <span className="text-[10px] font-black tracking-widest text-indigo-600 dark:text-indigo-400 uppercase flex items-center gap-1">
                                                                            <Play size={10} fill="currentColor" /> Active Session Footprint Trace
                                                                        </span>
                                                                        <span className="text-[10px] font-bold text-slate-400 font-mono">User ID: {u.userId}</span>
                                                                    </div>

                                                                    <div className="relative pl-6 border-l border-dashed border-indigo-200 dark:border-indigo-800 space-y-4 py-2 ml-2">
                                                                        {u.timeline.map((ev: any, evIdx: number) => {
                                                                            const isReg = evIdx === 0;
                                                                            const isPracticeMatch = firstPracticeEv && (
                                                                                ev === firstPracticeEv || 
                                                                                (ev.event === firstPracticeEv.event && ev.relativeTime === firstPracticeEv.relativeTime)
                                                                            );
                                                                            const stepColor = isPracticeMatch ? '#10b981' : ev.status === 'success' ? '#059669' : ev.status === 'danger' ? '#e11d48' : '#4338ca';
                                                                            
                                                                            return (
                                                                                <div key={evIdx} className="relative flex items-start justify-between gap-4">
                                                                                    <span className="absolute -left-[31px] top-1 w-2.5 h-2.5 rounded-full border border-white dark:border-slate-900" style={{ backgroundColor: stepColor }} />
                                                                                    
                                                                                    <div className="space-y-0.5">
                                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                                            <p className="text-xs font-extrabold text-slate-900 dark:text-slate-100 leading-tight">{ev.event}</p>
                                                                                            {isPracticeMatch && (
                                                                                                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 rounded-full text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1 shadow-2xs">
                                                                                                    <Zap size={10} className="fill-emerald-600 text-emerald-600" /> First Practice Entry
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        {isReg && <p className="text-[10px] text-slate-400 font-semibold leading-relaxed">T_0 timestamp: {new Date(ev.timestamp || u.joinedAt).toLocaleTimeString()}</p>}
                                                                                    </div>

                                                                                    <div className="text-right shrink-0 whitespace-nowrap">
                                                                                        <span className={`text-[10px] font-bold font-mono tracking-tight px-1.5 py-0.5 rounded border ${
                                                                                            isPracticeMatch 
                                                                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 font-extrabold' 
                                                                                                : 'bg-indigo-50 text-indigo-600 border-indigo-100/50 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800'
                                                                                        }`}>
                                                                                            +{ev.relativeTime}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>

                                                                    {u.status === 'Dropped off' && (
                                                                        <div className="flex items-center gap-2 p-3 bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 border border-rose-100 dark:border-rose-900/40 rounded-xl text-[10px]">
                                                                            <Info size={14} className="shrink-0 text-rose-500" />
                                                                            <span>
                                                                                <strong>Friction Drop Point Detected:</strong> {u.name} abandoned after completing <strong>{u.lastSuccessfulStep}</strong>. No subsequent clicks or duration pulses were logged.
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Registered Users Pagination Bar */}
                        <div className="mt-4">
                            <PaginationBar
                                currentPage={data?.pagination?.registered?.page || registeredPage}
                                totalPages={data?.pagination?.registered?.totalPages || Math.max(1, Math.ceil((data?.pagination?.registered?.total || (data.users || []).length) / registeredLimit))}
                                totalRecords={data?.pagination?.registered?.total || (data.users || []).length}
                                limit={registeredLimit}
                                sortOrder={registeredSort}
                                isLoading={isRefreshing}
                                onPageChange={(p) => setRegisteredPage(p)}
                                onLimitChange={(l) => {
                                    setRegisteredLimit(l);
                                    setRegisteredPage(1);
                                }}
                                onSortChange={(s) => {
                                    setRegisteredSort(s);
                                    setRegisteredPage(1);
                                }}
                                label="registered user journeys"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
